import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from './Layout';
import { useTheme } from '../contexts/ThemeContext';
import {
  Megaphone, Plus, Trash2, Edit2, Clock, Play, Pause,
  Upload, X, Film, Shuffle, AlignJustify, CheckCircle2,
  AlertCircle, FileVideo
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

// ── Constants ───────────────────────────────────────────────────────────────────
const MAX_FILES      = 10;
const MAX_FILE_BYTES = 500 * 1024 * 1024; // 500 MB per file
const ALLOWED_EXT    = /\.(mp4|mov|avi|mkv|webm)$/i;

// ── Helpers ─────────────────────────────────────────────────────────────────────
const formatDuration = (s) => {
  if (!s) return '00:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const formatSize = (bytes) => {
  if (!bytes) return '0 MB';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 ** 3)).toFixed(1)} GB`;
  return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
};

const getVideoCount = (spot) => {
  if (!spot.video_files) return 1;
  try {
    const arr = JSON.parse(spot.video_files);
    return Array.isArray(arr) ? arr.length : 1;
  } catch { return 1; }
};

// ── ExistingVideoItem (saved video already on server) ──────────────────────────
function ExistingVideoItem({ video, onRemove, isDark }) {
  const name = (video.original_name || '').replace(/\.[^/.]+$/, '').trim()
    || video.file_path?.split('/').pop() || 'Video';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '10px 12px',
      background: isDark ? 'rgba(99,102,241,0.07)' : 'rgba(99,102,241,0.05)',
      borderRadius: '10px',
      border: isDark ? '1px solid rgba(99,102,241,0.25)' : '1px solid rgba(99,102,241,0.2)',
      marginBottom: '6px'
    }}>
      <FileVideo size={18} style={{ color: '#818cf8', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ color: isDark ? '#f3f4f6' : '#111827', fontSize: '13px', fontWeight: 500, margin: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </p>
        {video.duration > 0 && (
          <p style={{ color: '#6b7280', fontSize: '11px', margin: '2px 0 0' }}>
            {formatDuration(video.duration)}
          </p>
        )}
      </div>
      <span style={{ fontSize: '10px', color: '#818cf8', background: 'rgba(99,102,241,0.15)',
                     padding: '2px 7px', borderRadius: '5px', flexShrink: 0 }}>guardado</span>
      <button
        type="button"
        onClick={() => onRemove(video.file_path)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#6b7280', padding: '2px', display: 'flex',
          alignItems: 'center', flexShrink: 0, transition: 'color 0.2s'
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
        onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
        title="Quitar video"
      >
        <X size={15} />
      </button>
    </div>
  );
}

// ── FileItem (one row in the file list) ────────────────────────────────────────
function FileItem({ item, onRemove, uploading, isDark }) {
  const statusColor = {
    pending:   '#6b7280',
    uploading: '#818cf8',
    done:      '#34d399',
    error:     '#f87171'
  }[item.status] || '#6b7280';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '10px 12px',
      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      borderRadius: '10px',
      border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
      marginBottom: '6px',
      transition: 'border-color 0.2s'
    }}>
      <FileVideo size={18} style={{ color: '#818cf8', flexShrink: 0 }} />

      {/* File name + size */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ color: isDark ? '#f3f4f6' : '#111827', fontSize: '13px', fontWeight: 500, margin: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.file.name}
        </p>
        <p style={{ color: '#6b7280', fontSize: '11px', margin: '2px 0 0' }}>
          {formatSize(item.file.size)}
        </p>
      </div>

      {/* Progress / status */}
      {uploading && item.status === 'uploading' && (
        <div style={{ width: '80px' }}>
          <div style={{ height: '4px', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${item.progress}%`,
              background: 'linear-gradient(90deg, #6366f1, #7c3aed)',
              borderRadius: '2px', transition: 'width 0.3s'
            }} />
          </div>
          <p style={{ color: '#818cf8', fontSize: '10px', textAlign: 'right', margin: '2px 0 0' }}>
            {item.progress}%
          </p>
        </div>
      )}

      {item.status === 'done' && (
        <CheckCircle2 size={16} style={{ color: '#34d399', flexShrink: 0 }} />
      )}
      {item.status === 'error' && (
        <AlertCircle size={16} style={{ color: '#f87171', flexShrink: 0 }} />
      )}

      {/* Remove button */}
      {!uploading && (
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#6b7280', padding: '2px', display: 'flex',
            alignItems: 'center', flexShrink: 0,
            transition: 'color 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
          onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
        >
          <X size={15} />
        </button>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
function Spots({ user, onLogout }) {
  const { isDark } = useTheme();
  const [spots, setSpots]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [dragOver, setDragOver]     = useState(false);

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name:            '',
    description:     '',
    interval_videos: 3,
    play_mode:       'sequential',
    is_active:       true
  });

  // Each entry: { id, file, status: 'pending'|'uploading'|'done'|'error', progress }
  const [selectedFiles, setSelectedFiles] = useState([]);
  // Existing server-side videos when editing (from spot.video_files)
  const [existingVideos, setExistingVideos] = useState([]);

  useEffect(() => { fetchSpots(); }, []);

  const fetchSpots = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/spots', { headers: { Authorization: `Bearer ${token}` } });
      setSpots(res.data.spots || []);
    } catch {
      toast.error('Error al cargar spots publicitarios');
    } finally {
      setLoading(false);
    }
  };

  // ── File validation & add ──────────────────────────────────────────────────
  const addFiles = useCallback((rawFiles) => {
    const remaining = MAX_FILES - selectedFiles.length;
    if (remaining <= 0) {
      toast.warning(`Máximo ${MAX_FILES} videos por spot`);
      return;
    }

    const toProcess = Array.from(rawFiles).slice(0, remaining);
    const validated = [];

    for (const file of toProcess) {
      if (!file.type.startsWith('video/') && !ALLOWED_EXT.test(file.name)) {
        toast.error(`${file.name}: formato no soportado`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name}: supera el límite de ${formatSize(MAX_FILE_BYTES)}`);
        continue;
      }
      validated.push({ id: `${Date.now()}-${Math.random()}`, file, status: 'pending', progress: 0 });
    }

    if (validated.length > 0) {
      setSelectedFiles(prev => [...prev, ...validated]);
    }
  }, [selectedFiles.length]);

  const removeFile = (id) => setSelectedFiles(prev => prev.filter(f => f.id !== id));
  const removeExistingVideo = (filePath) => setExistingVideos(prev => prev.filter(v => v.file_path !== filePath));

  // ── Drag & drop ─────────────────────────────────────────────────────────────
  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = ()  => setDragOver(false);
  const handleDrop      = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingSpot && selectedFiles.length === 0) {
      toast.error('Selecciona al menos un archivo de video');
      return;
    }
    if (editingSpot && existingVideos.length === 0 && selectedFiles.length === 0) {
      toast.error('El spot debe tener al menos un video');
      return;
    }

    const token = localStorage.getItem('token');
    const fd    = new FormData();
    fd.append('name',            formData.name);
    fd.append('description',     formData.description);
    fd.append('interval_videos', formData.interval_videos);
    fd.append('play_mode',       formData.play_mode);
    fd.append('is_active',       formData.is_active);

    selectedFiles.forEach(item => fd.append('videos', item.file));
    // When editing, always send keep_files so backend knows which existing videos to retain
    if (editingSpot) {
      fd.append('keep_files', JSON.stringify(existingVideos));
    }

    // Mark all as uploading
    setSelectedFiles(prev => prev.map(f => ({ ...f, status: 'uploading', progress: 0 })));
    setUploading(true);
    setOverallProgress(0);

    try {
      const onProgress = (e) => {
        const pct = Math.round((e.loaded * 100) / e.total);
        setOverallProgress(pct);
        setSelectedFiles(prev => prev.map(f => ({ ...f, progress: pct })));
      };

      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' };

      if (editingSpot) {
        await axios.put(`/api/spots/${editingSpot.id}`, fd, { headers, onUploadProgress: onProgress });
        toast.success('Spot actualizado correctamente');
      } else {
        await axios.post('/api/spots', fd, { headers, onUploadProgress: onProgress });
        toast.success('Spot creado correctamente');
      }

      setSelectedFiles(prev => prev.map(f => ({ ...f, status: 'done', progress: 100 })));

      setTimeout(() => {
        closeModal();
        fetchSpots();
      }, 600);
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al guardar spot';
      toast.error(msg);
      setSelectedFiles(prev => prev.map(f => ({ ...f, status: 'error' })));
    } finally {
      setUploading(false);
    }
  };

  // ── Delete / toggle ──────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este spot publicitario?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/spots/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Spot eliminado');
      fetchSpots();
    } catch { toast.error('Error al eliminar spot'); }
  };

  const handleToggleActive = async (spot) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/spots/${spot.id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(spot.is_active ? 'Spot desactivado' : 'Spot activado');
      fetchSpots();
    } catch { toast.error('Error al cambiar estado'); }
  };

  // ── Modal helpers ────────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setEditingSpot(null);
    setFormData({ name: '', description: '', interval_videos: 5, play_mode: 'sequential', is_active: true });
    setSelectedFiles([]);
    setOverallProgress(0);
    setShowModal(true);
  };

  const openEditModal = (spot) => {
    setEditingSpot(spot);
    setFormData({
      name:            spot.name,
      description:     spot.description || '',
      interval_videos: spot.interval_videos || 5,
      play_mode:       spot.play_mode || 'sequential',
      is_active:       spot.is_active
    });
    // Parse existing videos from the spot
    let existing = [];
    if (spot.video_files) {
      try { existing = JSON.parse(spot.video_files); } catch { existing = []; }
    } else if (spot.file_path) {
      existing = [{ file_path: spot.file_path, duration: spot.duration || 0, original_name: '' }];
    }
    setExistingVideos(existing);
    setSelectedFiles([]);
    setOverallProgress(0);
    setShowModal(true);
  };

  const closeModal = () => {
    if (uploading) return;
    setShowModal(false);
    setEditingSpot(null);
    setSelectedFiles([]);
    setExistingVideos([]);
    setOverallProgress(0);
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center items-center h-screen">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-accent-indigo rounded-full animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout user={user} onLogout={onLogout}>
      <style>{`
        @keyframes spotsSpin { to { transform: rotate(360deg); } }
        .spots-input {
          width: 100%; padding: 13px 16px;
          background: ${isDark ? '#1a1e2e' : '#ffffff'};
          border: 1px solid ${isDark ? '#252837' : 'rgba(0,0,0,0.12)'};
          border-radius: 10px;
          color: ${isDark ? '#f3f4f6' : '#111827'};
          font-size: 14px; outline: none; box-sizing: border-box;
          font-family: inherit; transition: border-color 0.2s, box-shadow 0.2s;
        }
        .spots-input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.18);
        }
        .spots-input::placeholder { color: ${isDark ? '#4b5563' : 'rgba(0,0,0,0.35)'}; }
        .spots-select { appearance: none; cursor: pointer; }
        .spots-btn-primary {
          flex: 1; padding: 13px 20px;
          background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
          border: none; border-radius: 10px; color: white;
          font-size: 14px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: opacity 0.2s, transform 0.1s; font-family: inherit;
        }
        .spots-btn-primary:hover:not(:disabled) { opacity: 0.9; }
        .spots-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .spots-btn-cancel {
          padding: 13px 24px;
          background: ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
          border: 1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'};
          border-radius: 10px;
          color: ${isDark ? '#9ca3af' : '#6B7280'};
          font-size: 14px; font-weight: 500; cursor: pointer;
          transition: background 0.2s, color 0.2s; font-family: inherit;
        }
        .spots-btn-cancel:hover {
          background: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
          color: ${isDark ? 'white' : '#111827'};
        }
        .spots-dropzone {
          border: 2px dashed ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'};
          border-radius: 12px; padding: 28px 20px;
          text-align: center; cursor: pointer;
          transition: border-color 0.2s, background 0.2s;
          background: ${isDark ? 'rgba(26,31,46,0.6)' : 'rgba(0,0,0,0.03)'};
        }
        .spots-dropzone:hover, .spots-dropzone.dragover {
          border-color: rgba(99,102,241,0.6);
          background: rgba(99,102,241,0.06);
        }
        .spots-play-mode-btn {
          flex: 1; padding: 10px 14px;
          border-radius: 9px;
          border: 1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.10)'};
          background: ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
          color: ${isDark ? '#9ca3af' : '#6B7280'};
          font-size: 13px; font-weight: 500; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: all 0.2s; font-family: inherit;
        }
        .spots-play-mode-btn.active {
          background: rgba(99,102,241,0.18);
          border-color: rgba(99,102,241,0.5);
          color: #a5b4fc;
        }
        .spots-spinner {
          width: 18px; height: 18px;
          border: 2px solid rgba(255,255,255,0.25);
          border-top-color: white; border-radius: 50%;
          animation: spotsSpin 0.8s linear infinite;
        }
      `}</style>

      <div className="p-2.5 min-h-screen max-w-[1600px] mx-auto">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1">Spots Publicitarios</h1>
            <p className="text-gray-400 text-sm">
              Inserta publicidades cada N videos en tus playlists, en orden o de forma aleatoria.
            </p>
          </div>
          <button
            className="btn-primary-gradient flex-shrink-0"
            onClick={openCreateModal}>
            <Plus size={18} /> Agregar Spot
          </button>
        </div>

        {/* ── Grid (spots + add card) ── */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {spots.map((spot) => {
            const videoCount = getVideoCount(spot);
            const isRandom   = spot.play_mode === 'random';
            return (
              <div key={spot.id} className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1a1f2e] p-6 transition-all duration-300 shadow-sm dark:shadow-none">
                {/* Card header */}
                <div className="flex justify-between items-start mb-5">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                      <Megaphone className="text-amber-400" size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{spot.name}</h3>
                      <p className="text-xs text-gray-500 truncate">{spot.description || 'Sin descripción'}</p>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border flex-shrink-0 ml-2 ${
                    spot.is_active
                      ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                      : 'bg-gray-500/15 text-gray-400 border-gray-500/30'
                  }`}>
                    {spot.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-black/5 dark:bg-black/30 rounded-xl p-2.5 border border-black/8 dark:border-white/10">
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1 font-semibold tracking-wider uppercase">Duración</div>
                    <div className="text-gray-900 dark:text-white text-sm font-semibold flex items-center gap-1">
                      <Clock size={11} className="text-indigo-400" />
                      {formatDuration(spot.duration)}
                    </div>
                  </div>
                  <div className="bg-black/5 dark:bg-black/30 rounded-xl p-2.5 border border-black/8 dark:border-white/10">
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1 font-semibold tracking-wider uppercase">Intervalo</div>
                    <div className="text-gray-900 dark:text-white text-sm font-semibold">c/ {spot.interval_videos || 5} vids</div>
                  </div>
                  <div className="bg-black/5 dark:bg-black/30 rounded-xl p-2.5 border border-black/8 dark:border-white/10">
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1 font-semibold tracking-wider uppercase">Modo</div>
                    <div className="text-gray-900 dark:text-white text-sm font-semibold flex items-center gap-1">
                      {isRandom ? <Shuffle size={11} className="text-purple-400" /> : <AlignJustify size={11} className="text-indigo-400" />}
                      {isRandom ? 'Random' : 'Orden'}
                    </div>
                  </div>
                </div>

                {/* Videos count badge */}
                <div className="flex items-center gap-2 mb-5">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/5 dark:bg-black/30 border border-black/8 dark:border-white/10">
                    <Film size={11} className="text-gray-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">{videoCount === 1 ? '1 video' : `${videoCount} videos`}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ${
                      spot.is_active
                        ? 'bg-amber-500/15 hover:bg-amber-500/25 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                        : 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                    }`}
                    onClick={() => handleToggleActive(spot)}>
                    {spot.is_active ? <Pause size={14} /> : <Play size={14} />}
                    {spot.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-black/10 dark:border-white/10 transition-all duration-200"
                    onClick={() => openEditModal(spot)}>
                    <Edit2 size={15} />
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-black/5 dark:bg-white/5 hover:bg-red-500/15 dark:hover:bg-red-600/20 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 border border-black/10 dark:border-white/10 hover:border-red-500/30 dark:hover:border-red-600/30 transition-all duration-200"
                    onClick={() => handleDelete(spot.id)}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add new spot card */}
          <div
            className="rounded-2xl border-2 border-dashed border-black/15 dark:border-white/15 hover:border-accent-purple/40 p-6 flex flex-col items-center justify-center cursor-pointer min-h-[280px] group transition-all duration-300 hover:bg-accent-purple/5"
            onClick={openCreateModal}
          >
            <div className="w-16 h-16 rounded-full border-2 border-black/15 dark:border-white/20 group-hover:border-accent-purple/50 flex items-center justify-center mb-5 transition-all duration-300">
              <Plus size={28} className="text-gray-400 group-hover:text-accent-purple transition-colors" />
            </div>
            <h3 className="text-base font-semibold text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors mb-2">
              Agregar Nuevo Spot
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-600 text-center leading-relaxed">
              Inserta publicidades automáticamente en tus playlists
            </p>
          </div>
        </div>

        {/* ────────────────── MODAL ────────────────── */}
        {showModal && (
          <div
            className="modal-overlay-premium animate-fadeIn"
            onClick={closeModal}
          >
            <div
              className="modal-premium animate-scale-in"
              style={{ maxWidth: '600px', width: '100%' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '10px',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(124,58,237,0.3))',
                    border: '1px solid rgba(99,102,241,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Megaphone size={18} style={{ color: '#818cf8' }} />
                  </div>
                  <h2 style={{ color: isDark ? 'white' : '#111827', fontSize: '20px', fontWeight: 700, margin: 0 }}>
                    {editingSpot ? 'Editar Spot' : 'Nuevo Spot Publicitario'}
                  </h2>
                </div>
                <button
                  onClick={closeModal}
                  disabled={uploading}
                  style={{
                    background: 'none', border: 'none', cursor: uploading ? 'not-allowed' : 'pointer',
                    color: '#6b7280', padding: '6px', borderRadius: '8px',
                    display: 'flex', alignItems: 'center'
                  }}
                >
                  <X size={22} />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Name */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: isDark ? '#e5e7eb' : '#374151', fontSize: '13px', fontWeight: 600, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Nombre del Spot
                  </label>
                  <input
                    type="text"
                    className="spots-input"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Publicidad Coca-Cola"
                    required
                    disabled={uploading}
                  />
                </div>

                {/* Description */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: isDark ? '#e5e7eb' : '#374151', fontSize: '13px', fontWeight: 600, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Descripción
                  </label>
                  <textarea
                    className="spots-input"
                    rows={2}
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción del spot publicitario"
                    disabled={uploading}
                    style={{ resize: 'none' }}
                  />
                </div>

                {/* Interval + Play mode (side by side) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                  {/* Interval */}
                  <div>
                    <label style={{ display: 'block', color: isDark ? '#e5e7eb' : '#374151', fontSize: '13px', fontWeight: 600, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Intervalo
                    </label>
                    <div style={{ position: 'relative' }}>
                      <select
                        className="spots-input spots-select"
                        value={formData.interval_videos}
                        onChange={e => setFormData({ ...formData, interval_videos: parseInt(e.target.value) })}
                        disabled={uploading}
                        style={{ paddingRight: '36px' }}
                      >
                        <option value={3}>Cada 3 videos</option>
                        <option value={6}>Cada 6 videos</option>
                        <option value={9}>Cada 9 videos</option>
                        <option value={12}>Cada 12 videos</option>
                      </select>
                      <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }}>
                        ▾
                      </div>
                    </div>
                  </div>

                  {/* Play mode */}
                  <div>
                    <label style={{ display: 'block', color: isDark ? '#e5e7eb' : '#374151', fontSize: '13px', fontWeight: 600, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Modo de Reproducción
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className={`spots-play-mode-btn ${formData.play_mode === 'sequential' ? 'active' : ''}`}
                        onClick={() => setFormData({ ...formData, play_mode: 'sequential' })}
                        disabled={uploading}
                      >
                        <AlignJustify size={14} />
                        En Orden
                      </button>
                      <button
                        type="button"
                        className={`spots-play-mode-btn ${formData.play_mode === 'random' ? 'active' : ''}`}
                        onClick={() => setFormData({ ...formData, play_mode: 'random' })}
                        disabled={uploading}
                      >
                        <Shuffle size={14} />
                        Aleatorio
                      </button>
                    </div>
                  </div>
                </div>

                {/* Dropzone */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px' }}>
                    <label style={{ color: '#e5e7eb', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {editingSpot ? 'Agregar Videos' : `Archivos de Video (máx. ${MAX_FILES})`}
                    </label>
                    {selectedFiles.length > 0 && (
                      <span style={{ color: '#818cf8', fontSize: '12px', fontWeight: 500 }}>
                        {selectedFiles.length}/{MAX_FILES} archivos
                      </span>
                    )}
                  </div>

                  {/* Existing videos list (edit mode) */}
                  {editingSpot && existingVideos.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <p style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 600,
                                  textTransform: 'uppercase', letterSpacing: '0.04em',
                                  margin: '0 0 6px' }}>
                        Videos actuales ({existingVideos.length})
                      </p>
                      {existingVideos.map(v => (
                        <ExistingVideoItem
                          key={v.file_path}
                          video={v}
                          onRemove={removeExistingVideo}
                          isDark={isDark}
                        />
                      ))}
                    </div>
                  )}
                  {editingSpot && existingVideos.length === 0 && selectedFiles.length === 0 && (
                    <div style={{
                      padding: '8px 12px', borderRadius: '8px', marginBottom: '10px',
                      background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
                      display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                      <AlertCircle size={14} style={{ color: '#f87171', flexShrink: 0 }} />
                      <span style={{ color: '#fca5a5', fontSize: '12px' }}>
                        Sin videos. Agrega al menos uno antes de guardar.
                      </span>
                    </div>
                  )}

                  {/* Drop zone */}
                  {selectedFiles.length < MAX_FILES && !uploading && (
                    <div
                      className={`spots-dropzone ${dragOver ? 'dragover' : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm,.mp4,.mov,.avi,.mkv,.webm"
                        style={{ display: 'none' }}
                        onChange={e => addFiles(e.target.files)}
                      />
                      <div style={{
                        width: '48px', height: '48px', borderRadius: '12px',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 12px'
                      }}>
                        <Upload size={22} style={{ color: '#818cf8' }} />
                      </div>
                      <p style={{ color: '#e5e7eb', fontWeight: 600, fontSize: '14px', margin: '0 0 4px' }}>
                        {selectedFiles.length === 0
                          ? 'Arrastra videos o haz clic para seleccionar'
                          : `Añadir más videos (${MAX_FILES - selectedFiles.length} restantes)`}
                      </p>
                      <p style={{ color: '#6b7280', fontSize: '12px', margin: 0 }}>
                        MP4, MOV, AVI, MKV · Máx. {formatSize(MAX_FILE_BYTES)} por archivo
                      </p>
                    </div>
                  )}

                  {/* File list */}
                  {selectedFiles.length > 0 && (
                    <div style={{ marginTop: '10px', maxHeight: '220px', overflowY: 'auto' }}>
                      {selectedFiles.map(item => (
                        <FileItem
                          key={item.id}
                          item={item}
                          onRemove={removeFile}
                          uploading={uploading}
                          isDark={isDark}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Overall progress bar */}
                {uploading && (
                  <div style={{
                    padding: '12px 14px', borderRadius: '10px', marginBottom: '16px',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: '#9ca3af', fontSize: '13px' }}>Subiendo archivos…</span>
                      <span style={{ color: '#818cf8', fontSize: '13px', fontWeight: 700 }}>{overallProgress}%</span>
                    </div>
                    <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${overallProgress}%`,
                        background: 'linear-gradient(90deg, #6366f1, #7c3aed)',
                        borderRadius: '3px', transition: 'width 0.3s'
                      }} />
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
                  <button type="submit" className="spots-btn-primary" disabled={uploading}>
                    {uploading
                      ? <><div className="spots-spinner" /> Subiendo…</>
                      : <>{editingSpot ? <Edit2 size={16} /> : <Plus size={16} />} {editingSpot ? 'Guardar Cambios' : 'Crear Spot'}</>
                    }
                  </button>
                  <button type="button" className="spots-btn-cancel" onClick={closeModal} disabled={uploading}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Spots;
